import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  Tooltip,
  Row,
  Col,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
  FileTextOutlined,
  WhatsAppOutlined,
  MailOutlined,
  UploadOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  subscribeToInvoices,
  deleteInvoice,
  updateInvoiceStatus,
  getInvoiceStats,
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_TYPE_LABELS
} from '../../firebase/invoiceService';
import { subscribeToQuotations } from '../../firebase/quotationService';
import { generateInvoicePDF } from './InvoicePDF';
import PDFDownloadDropdown from './PDFDownloadDropdown';
import InvoiceForm from './InvoiceForm';
import WhatsAppModal from '../WhatsApp/WhatsAppModal';
import EmailModal from '../Email/EmailModal';
import CSVInvoiceImport from '../Admin/CSVInvoiceImport';
import { formatMultipleDatesForPDF, shouldEnableMultipleDates } from '../../utils/multipleDateUtils';
import { usesUnifiedDates, getDatesForPDF } from '../../utils/unifiedDateStorage';
import { updateInvoiceAccountingStatus } from '../../firebase/invoiceService';
import './InvoiceList.css';

const { Option } = Select;
const { Title, Text } = Typography;

const InvoiceList = ({ dateRange, onDateRangeChange }) => {
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [accountingFilter, setAccountingFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [invoicesUnsubscribe, setInvoicesUnsubscribe] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQuotationSelectModal, setShowQuotationSelectModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [whatsAppInvoice, setWhatsAppInvoice] = useState(null);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  // Subscribe to invoices when dateRange changes
  useEffect(() => {
    // Always subscribe, even when dateRange is null (for "All Time")
    if (!dateRange) {
      return; // Wait for dateRange to be initialized
    }

    // Unsubscribe from previous subscription
    if (invoicesUnsubscribe) {
      invoicesUnsubscribe();
    }
    
    // Create new subscription with date range
    // When both dates are null, this means "All Time" - fetch all invoices
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    
    console.log('📊 InvoiceList: Setting up subscription with date range:', {
      label: dateRange.label,
      hasStartDate: !!dateRange.startDate,
      hasEndDate: !!dateRange.endDate,
      isAllTime: !dateRange.startDate && !dateRange.endDate
    });
    
    const newUnsubscribe = subscribeToInvoices((invoicesData) => {
      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
      setLoading(false);
    }, options);
    
    setInvoicesUnsubscribe(() => newUnsubscribe);
    
    // Update stats with date range
    getInvoiceStats(options).then(setStats);

    return () => {
      if (newUnsubscribe) {
        newUnsubscribe();
      }
    };
  }, [dateRange]);

  useEffect(() => {
    let currentInvoices = invoices;

    // Subscribe to quotations data for creating invoices from quotations
    const unsubscribeQuotations = subscribeToQuotations((quotationsData) => {
      // Filter to only non-rejected quotations that don't already have invoices
      const availableQuotations = quotationsData.filter(q => 
        q.status !== 'rejected' && 
        !currentInvoices.some(i => i.quotationId === q.id)
      );
      setQuotations(availableQuotations);
    });

    return () => {
      unsubscribeQuotations();
    };
  }, [invoices]);

  useEffect(() => {
    // Filter invoices based on search term, status, type, and accounting
    let filtered = invoices;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice =>
        invoice.companyName?.toLowerCase().includes(searchLower) ||
        invoice.attn?.toLowerCase().includes(searchLower) ||
        invoice.email?.toLowerCase().includes(searchLower) ||
        invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
        invoice.originalInvoiceNumber?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.invoiceType === typeFilter);
    }

    if (accountingFilter !== 'all') {
      if (accountingFilter === 'submitted') {
        filtered = filtered.filter(invoice => invoice.submitToAccounting === true);
      } else if (accountingFilter === 'pending') {
        filtered = filtered.filter(invoice => invoice.submitToAccounting !== true);
      }
    }

    setFilteredInvoices(filtered);
  }, [invoices, searchTerm, statusFilter, typeFilter, accountingFilter]);

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setSelectedQuotation(null);
    setShowCreateModal(true);
  };

  const handleCreateFromQuotation = () => {
    if (quotations.length === 0) {
      message.warning('No quotations available for invoicing');
      return;
    }
    setShowQuotationSelectModal(true);
  };

  const handleQuotationSelect = (quotation) => {
    setSelectedQuotation(quotation);
    setShowQuotationSelectModal(false);
    setShowCreateModal(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    Modal.confirm({
      title: 'Delete Invoice',
      content: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const success = await deleteInvoice(invoiceId);
        if (success) {
          message.success('Invoice deleted successfully');
          // Update stats with current date range
          const options = {};
          if (dateRange.startDate) {
            options.startDate = dateRange.startDate;
          }
          if (dateRange.endDate) {
            options.endDate = dateRange.endDate;
          }
          getInvoiceStats(options).then(setStats);
        } else {
          message.error('Failed to delete invoice');
        }
      }
    });
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    const success = await updateInvoiceStatus(invoiceId, newStatus);
    if (success) {
      message.success('Status updated successfully');
      // Update stats with current date range
      const options = {};
      if (dateRange.startDate) {
        options.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        options.endDate = dateRange.endDate;
      }
      getInvoiceStats(options).then(setStats);
    } else {
      message.error('Failed to update status');
    }
  };

  const handleGeneratePDF = async (invoice) => {
    try {
      const invoiceData = {
        invoiceNumber: invoice.invoiceNumber,
        date: moment().format('DD/MM/YY'),
        dueDate: invoice.dueDate ? moment(invoice.dueDate).format('DD/MM/YYYY') : null,
        companyName: invoice.companyName,
        attn: invoice.attn,
        tel: invoice.tel,
        email: invoice.email,
        billingAddress: invoice.billingAddress,
        siteAddress: invoice.siteAddress,
        invoiceType: invoice.invoiceType,
        dates: invoice.dates || [], // Add unified dates support
        
        // Legacy service dates (for backward compatibility)
        serviceStartDate: invoice.serviceStartDate ? moment(invoice.serviceStartDate).format('DD/MM/YYYY') : null,
        serviceEndDate: invoice.serviceEndDate ? moment(invoice.serviceEndDate).format('DD/MM/YYYY') : null,
        
        // Purchase type date fields
        leadTime: invoice.leadTime ? moment(invoice.leadTime).format('DD/MM/YYYY') : null,
        leadTimeInputType: invoice.leadTimeInputType,
        leadTimeMonths: invoice.leadTimeMonths,
        
        // Scaffolding service type date fields
        serviceInputType: invoice.serviceInputType,
        serviceDate: invoice.serviceDate ? moment(invoice.serviceDate).format('DD/MM/YYYY') : null,
        
        // Rental type date fields - REMOVED: Using unified dates system instead
        
        // Damaged items specific fields
        collectionDate: invoice.collectionDate ? moment(invoice.collectionDate).format('DD/MM/YYYY') : null,
        originalInvoiceNumber: invoice.originalInvoiceNumber || null,
        
        poNumber: invoice.poNumber,
        paymentTerms: invoice.paymentTerms,
        customPaymentTerms: invoice.customPaymentTerms,
        lineItems: invoice.lineItems || [],
        additionalServices: invoice.additionalServices || {},
        discount: invoice.discount,
        discountAmount: invoice.discountAmount,
        discountType: invoice.discount?.type,
        // GST support
        gst: invoice.gst || { enabled: false, percentage: 9 },
        gstAmount: invoice.gstAmount || 0,
        subtotal: invoice.subtotal || 0,
        additionalServicesTotal: invoice.additionalServicesTotal || 0,
        grandTotal: invoice.grandTotal || 0,
        notes: invoice.notes
      };

      await generateInvoicePDF(invoiceData);
      message.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const handleSaveInvoice = (invoice) => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedInvoice(null);
    setSelectedQuotation(null);
    // Update stats with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getInvoiceStats(options).then(setStats);
  };

  const handleSendWhatsApp = (invoice) => {
    setWhatsAppInvoice(invoice);
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppSendSuccess = () => {
    // Refresh the data to get updated status with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getInvoiceStats(options).then(setStats);
  };

  const handleSendEmail = (invoice) => {
    setEmailInvoice(invoice);
    setShowEmailModal(true);
  };

  const handleEmailSendSuccess = () => {
    setShowEmailModal(false);
    setEmailInvoice(null);
    // Refresh data to get updated status with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getInvoiceStats(options).then(setStats);
  };

  const handleCancelModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowQuotationSelectModal(false);
    setShowWhatsAppModal(false);
    setShowEmailModal(false);
    setShowCSVImportModal(false);
    setSelectedInvoice(null);
    setSelectedQuotation(null);
    setWhatsAppInvoice(null);
    setEmailInvoice(null);
  };

  const handleCSVImport = () => {
    setShowCSVImportModal(true);
  };

  const handleCSVImportSuccess = (results) => {
    setShowCSVImportModal(false);
    message.success(`Successfully imported ${results.successful} invoice(s)`);
    
    // Refresh stats with current date range
    const options = {};
    if (dateRange?.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange?.endDate) {
      options.endDate = dateRange.endDate;
    }
    getInvoiceStats(options).then(setStats);
  };

  const handleAccountingStatusToggle = async (invoiceId, currentStatus) => {
    const newStatus = !currentStatus;
    const success = await updateInvoiceAccountingStatus(invoiceId, newStatus);
    
    if (success) {
      message.success(`Accounting status ${newStatus ? 'submitted' : 'unsubmitted'}`);
    } else {
      message.error('Failed to update accounting status');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getStatusTag = (status) => {
    const color = INVOICE_STATUS_COLORS[status];
    const label = INVOICE_STATUS_LABELS[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const expandedRowRender = (record) => {
    const lineItems = record.lineItems || [];
    const additionalServices = record.additionalServices || {};
    
    const selectedServices = Object.entries(additionalServices)
      .filter(([key, service]) => service.selected)
      .map(([key, service]) => ({ key, ...service }));

    return (
      <div style={{ margin: '16px 0' }}>
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Card title="Customer Details" size="small">
              <p><strong>Company:</strong> {record.companyName}</p>
              <p><strong>Contact:</strong> {record.attn}</p>
              <p><strong>Phone:</strong> {record.tel}</p>
              <p><strong>Email:</strong> {record.email}</p>
              {record.siteAddress && <p><strong>Site Address:</strong> {record.siteAddress}</p>}
              {record.billingAddress && <p><strong>Billing Address:</strong> {record.billingAddress}</p>}
              <p><strong>Type:</strong> {INVOICE_TYPE_LABELS[record.invoiceType]}</p>
              {record.dueDate && <p><strong>Due Date:</strong> {moment(record.dueDate).format('DD/MM/YYYY')}</p>}
              
              {/* Purchase type dates */}
              {record.invoiceType === 'purchase' && record.leadTime && (
                <p><strong>Delivery Date:</strong> {moment(record.leadTime).format('DD/MM/YYYY')}</p>
              )}
              {record.invoiceType === 'purchase' && record.leadTimeInputType === 'months' && record.leadTimeMonths && (
                <p><strong>Lead Time:</strong> {record.leadTimeMonths} month{record.leadTimeMonths > 1 ? 's' : ''}</p>
              )}
              {record.invoiceType === 'purchase' && record.leadTimeInputType === 'tbc' && (
                <p><strong>Delivery Date:</strong> To be confirmed</p>
              )}
              
              {/* Scaffolding service type dates */}
              {record.invoiceType === 'scaffolding_service' && record.serviceInputType === 'date' && record.serviceDate && (
                <p><strong>Service Date:</strong> {moment(record.serviceDate).format('DD/MM/YYYY')}</p>
              )}
              {record.invoiceType === 'scaffolding_service' && record.serviceInputType === 'range' && record.serviceStartDate && record.serviceEndDate && (
                <p><strong>Service Period:</strong> {moment(record.serviceStartDate).format('DD/MM/YYYY')} - {moment(record.serviceEndDate).format('DD/MM/YYYY')}</p>
              )}
              {record.invoiceType === 'scaffolding_service' && record.serviceInputType === 'tbc' && (
                <p><strong>Service Date:</strong> To be confirmed</p>
              )}
              
              {/* Rental type dates */}
              {(record.invoiceType === 'rental' || record.invoiceType === 'rental_extension') && record.rentalInputType === 'date' && record.rentalStartDate && record.rentalEndDate && (
                <p><strong>Rental Period:</strong> {moment(record.rentalStartDate).format('DD/MM/YYYY')} - {moment(record.rentalEndDate).format('DD/MM/YYYY')}</p>
              )}
              {(record.invoiceType === 'rental' || record.invoiceType === 'rental_extension') && record.rentalInputType === 'duration' && record.rentalDuration && record.rentalDurationUnit && (
                <p><strong>Rental Duration:</strong> {record.rentalDuration} {record.rentalDurationUnit}</p>
              )}
              
              {/* Damaged items specific fields */}
              {record.invoiceType === 'damaged_items' && record.collectionDate && (
                <p><strong>Collection Date:</strong> {moment(record.collectionDate).format('DD/MM/YYYY')}</p>
              )}
              {record.invoiceType === 'damaged_items' && record.originalInvoiceNumber && (
                <p><strong>Original Invoice:</strong> {record.originalInvoiceNumber}</p>
              )}
              
              {/* Legacy service dates (for backward compatibility) */}
              {record.serviceStartDate && record.serviceEndDate && !record.serviceInputType && (
                <p><strong>Service Period:</strong> {moment(record.serviceStartDate).format('DD/MM/YYYY')} - {moment(record.serviceEndDate).format('DD/MM/YYYY')}</p>
              )}
              
              {record.poNumber && <p><strong>PO Number:</strong> {record.poNumber}</p>}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Pricing Summary" size="small">
              <p><strong>Subtotal:</strong> {formatCurrency(record.subtotal)}</p>
              <p><strong>Additional Services:</strong> {formatCurrency(record.additionalServicesTotal)}</p>
              {record.discountAmount && record.discountAmount > 0 && (
                <p><strong>Discount:</strong> <span style={{ color: '#52c41a' }}>-{formatCurrency(record.discountAmount)}</span></p>
              )}
              <p><strong>Grand Total:</strong> <Text strong>{formatCurrency(record.grandTotal)}</Text></p>
              <p><strong>Payment Terms:</strong> {record.customPaymentTerms || record.paymentTerms}</p>
            </Card>
          </Col>
        </Row>
        
        {lineItems.length > 0 && (
          <Card title="Line Items" size="small" style={{ marginTop: 16 }}>
            <Table
              dataSource={lineItems}
              pagination={false}
              size="small"
              columns={[
                { title: '#', dataIndex: 'id', key: 'id', width: 50 },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 100 },
                { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, render: formatCurrency },
                { title: 'Total', dataIndex: 'total', key: 'total', width: 120, render: formatCurrency }
              ]}
            />
          </Card>
        )}

        {selectedServices.length > 0 && (
          <Card title="Additional Services" size="small" style={{ marginTop: 16 }}>
            {selectedServices.map((service, index) => (
              <p key={index}>{service.key}: {formatCurrency(service.price)}</p>
            ))}
          </Card>
        )}

        {record.notes && (
          <Card title="Notes" size="small" style={{ marginTop: 16 }}>
            <p>{record.notes}</p>
          </Card>
        )}

        {record.internalRemarks && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#1890ff' }}>🔒</span>
                <span>Internal Remarks (Admin Only)</span>
              </div>
            } 
            size="small" 
            style={{ 
              marginTop: 16,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <div style={{ 
              marginBottom: 8, 
              fontSize: '11px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Private notes for internal reference only. Not visible in PDFs or client communications.
            </div>
            <p style={{ margin: 0, backgroundColor: '#fff', padding: '8px', borderRadius: '4px' }}>
              {record.internalRemarks}
            </p>
          </Card>
        )}

        {record.quotationNumber && (
          <Card title="Related Quotation" size="small" style={{ marginTop: 16 }}>
            <p><strong>Created from Quotation:</strong> {record.quotationNumber}</p>
          </Card>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 150,
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.quotationNumber && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
              From: {record.quotationNumber}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 200
    },
    {
      title: 'Contact',
      dataIndex: 'attn',
      key: 'attn',
      width: 150
    },
    {
      title: 'Type',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      width: 120,
      render: (type) => INVOICE_TYPE_LABELS[type] || type
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (_, record) => {
        // Use unified date system first
        if (usesUnifiedDates(record)) {
          return getDatesForPDF(record) || 'N/A';
        }

        // Fallback to legacy date system for backward compatibility
        // Check for multiple dates first
        if (shouldEnableMultipleDates(record)) {
          const multipleDates = record.multipleDates || record.multipleLeadTimes || record.multipleServiceDates || record.multipleRentalPeriods;
          const formattedMultipleDates = formatMultipleDatesForPDF(multipleDates, record.invoiceType);
          if (formattedMultipleDates) {
            return formattedMultipleDates;
          }
        }

        // Fallback to single date logic
        // Purchase type dates
        if (record.invoiceType === 'purchase') {
          if (record.leadTimeInputType === 'months' && record.leadTimeMonths) {
            return `${record.leadTimeMonths} month${record.leadTimeMonths > 1 ? 's' : ''}`;
          }
          if (record.leadTimeInputType === 'tbc') {
            return 'TBC';
          }
          if (record.leadTime) {
            return moment(record.leadTime).format('DD/MM/YYYY');
          }
        }

        // Scaffolding service type dates
        if (record.invoiceType === 'scaffolding_service') {
          if (record.serviceInputType === 'date' && record.serviceDate) {
            return moment(record.serviceDate).format('DD/MM/YYYY');
          }
          if (record.serviceInputType === 'range' && record.serviceStartDate && record.serviceEndDate) {
            return `${moment(record.serviceStartDate).format('DD/MM/YY')}-${moment(record.serviceEndDate).format('DD/MM/YY')}`;
          }
          if (record.serviceInputType === 'tbc') {
            return 'TBC';
          }
        }

                // Rental type dates - Use unified dates array if available
                if (record.invoiceType === 'rental' || record.invoiceType === 'rental_extension') {
                  // Check if we have unified dates first
                  if (record.dates && Array.isArray(record.dates) && record.dates.length > 0) {
                    const rentalDateEntry = record.dates.find(entry => entry.type === 'range' || entry.type === 'date');
                    if (rentalDateEntry) {
                      if (rentalDateEntry.type === 'range' && rentalDateEntry.value && rentalDateEntry.endValue) {
                        // Format as range: DD/MM/YY-DD/MM/YY
                        const startDate = moment(rentalDateEntry.value).format('DD/MM/YY');
                        const endDate = moment(rentalDateEntry.endValue).format('DD/MM/YY');
                        return `${startDate}-${endDate}`;
                      } else if (rentalDateEntry.type === 'date' && rentalDateEntry.value && rentalDateEntry.endValue) {
                        // Legacy support: 'date' type with endValue (should be 'range')
                        const startDate = moment(rentalDateEntry.value).format('DD/MM/YY');
                        const endDate = moment(rentalDateEntry.endValue).format('DD/MM/YY');
                        return `${startDate}-${endDate}`;
                      } else if (rentalDateEntry.type === 'date' && rentalDateEntry.value) {
                        return moment(rentalDateEntry.value).format('DD/MM/YYYY');
                      }
                    }
                  }

          // Fallback to legacy fields
          if (record.rentalInputType === 'date' && record.rentalStartDate && record.rentalEndDate) {
            return `${moment(record.rentalStartDate).format('DD/MM/YY')}-${moment(record.rentalEndDate).format('DD/MM/YY')}`;
          }
          if (record.rentalInputType === 'duration' && record.rentalDuration && record.rentalDurationUnit) {
            return `${record.rentalDuration} ${record.rentalDurationUnit}`;
          }
        }

        // Damaged items specific fields
        if (record.invoiceType === 'damaged_items' && record.collectionDate) {
          return moment(record.collectionDate).format('DD/MM/YYYY');
        }

        // Legacy service dates (for backward compatibility)
        if (record.serviceStartDate && record.serviceEndDate && !record.serviceInputType) {
          return `${moment(record.serviceStartDate).format('DD/MM/YY')}-${moment(record.serviceEndDate).format('DD/MM/YY')}`;
        }

        return 'N/A';
      }
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date) => date ? moment(date).format('DD/MM/YYYY') : 'N/A'
    },
    {
      title: 'Total Amount',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      width: 120,
      render: formatCurrency
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: 'Accounting',
      dataIndex: 'submitToAccounting',
      key: 'submitToAccounting',
      width: 100,
      render: (submitted, record) => (
        <Tooltip title={submitted ? "Submitted to accounting" : "Not submitted to accounting"}>
          <span
            style={{
              color: submitted ? '#52c41a' : '#d9d9d9',
              fontWeight: submitted ? 'bold' : 'normal',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {submitted ? <CheckCircleOutlined /> : null}
            {submitted ? '✓ Yes' : '○ No'}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: formatDate
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditInvoice(record)}
            />
          </Tooltip>
          <PDFDownloadDropdown 
            invoice={record} 
            size="small"
            showText={false}
            buttonProps={{ type: "text" }}
          />
          <Tooltip title={record.email ? "Send via Email" : "No email address available"}>
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleSendEmail(record)}
              disabled={!record.email}
              style={{
                color: record.email ? '#1890ff' : undefined
              }}
            />
          </Tooltip>
          <Tooltip title="Send WhatsApp">
            <Button
              type="text"
              icon={<WhatsAppOutlined />}
              onClick={() => handleSendWhatsApp(record)}
              style={{ color: '#25D366' }}
              disabled={!record.tel}
            />
          </Tooltip>
          <Select
            value={record.status}
            onChange={(value) => handleStatusChange(record.id, value)}
            size="small"
            style={{ width: 100 }}
          >
            {Object.entries(INVOICE_STATUS_LABELS).map(([key, label]) => (
              <Option key={key} value={key}>{label}</Option>
            ))}
          </Select>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteInvoice(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const quotationColumns = [
    {
      title: 'Quotation #',
      dataIndex: 'quotationNumber',
      key: 'quotationNumber',
      width: 150
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 200
    },
    {
      title: 'Total Amount',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      width: 120,
      render: formatCurrency
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: formatDate
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleQuotationSelect(record)}
        >
          Select
        </Button>
      )
    }
  ];

  // Helper function to get due date status
  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return null;
    const today = moment();
    const due = moment(dueDate);
    const daysUntilDue = due.diff(today, 'days');
    
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 7) return 'due-soon';
    return 'normal';
  };

  // Mobile card component
  const renderMobileCard = (invoice) => {
    const dueDateStatus = getDueDateStatus(invoice.dueDate);
    
    return (
      <div key={invoice.id} className="invoice-mobile-card">
        <div className="invoice-mobile-header">
          <div className="invoice-mobile-title">
            {invoice.invoiceNumber}
            {invoice.quotationNumber && (
              <div className="invoice-quotation-ref">
                From: {invoice.quotationNumber}
              </div>
            )}
          </div>
          <div className="invoice-mobile-meta">
            <span className="invoice-mobile-company">{invoice.companyName}</span>
            <span className="invoice-mobile-amount">{formatCurrency(invoice.grandTotal)}</span>
          </div>
        </div>
        
        <div className="invoice-mobile-body">
          <div className="invoice-mobile-row">
            <span className="invoice-mobile-label">Contact:</span>
            <span className="invoice-mobile-value">{invoice.attn}</span>
          </div>
          {invoice.siteAddress && (
            <div className="invoice-mobile-row">
              <span className="invoice-mobile-label">Site Address:</span>
              <span className="invoice-mobile-value">{invoice.siteAddress}</span>
            </div>
          )}
          <div className="invoice-mobile-row">
            <span className="invoice-mobile-label">Type:</span>
            <span className="invoice-mobile-value">{INVOICE_TYPE_LABELS[invoice.invoiceType] || invoice.invoiceType}</span>
          </div>
          <div className="invoice-mobile-row">
            <span className="invoice-mobile-label">Date:</span>
            <span className="invoice-mobile-value">
              {(() => {
                // Use unified date system first
                if (usesUnifiedDates(invoice)) {
                  return getDatesForPDF(invoice) || 'N/A';
                }

                // Fallback to legacy date system for backward compatibility
                // Check for multiple dates first
                if (shouldEnableMultipleDates(invoice)) {
                  const multipleDates = invoice.multipleDates || invoice.multipleLeadTimes || invoice.multipleServiceDates || invoice.multipleRentalPeriods;
                  const formattedMultipleDates = formatMultipleDatesForPDF(multipleDates, invoice.invoiceType);
                  if (formattedMultipleDates) {
                    return formattedMultipleDates;
                  }
                }

                // Fallback to single date logic
                // Purchase type dates
                if (invoice.invoiceType === 'purchase') {
                  if (invoice.leadTimeInputType === 'months' && invoice.leadTimeMonths) {
                    return `${invoice.leadTimeMonths} month${invoice.leadTimeMonths > 1 ? 's' : ''}`;
                  }
                  if (invoice.leadTimeInputType === 'tbc') {
                    return 'TBC';
                  }
                  if (invoice.leadTime) {
                    return moment(invoice.leadTime).format('DD/MM/YYYY');
                  }
                }

                // Scaffolding service type dates
                if (invoice.invoiceType === 'scaffolding_service') {
                  if (invoice.serviceInputType === 'date' && invoice.serviceDate) {
                    return moment(invoice.serviceDate).format('DD/MM/YYYY');
                  }
                  if (invoice.serviceInputType === 'range' && invoice.serviceStartDate && invoice.serviceEndDate) {
                    return `${moment(invoice.serviceStartDate).format('DD/MM/YY')}-${moment(invoice.serviceEndDate).format('DD/MM/YY')}`;
                  }
                  if (invoice.serviceInputType === 'tbc') {
                    return 'TBC';
                  }
                }

                // Rental type dates - Use unified dates array if available
                if (invoice.invoiceType === 'rental' || invoice.invoiceType === 'rental_extension') {
                  // Check if we have unified dates first
                  if (invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0) {
                    const rentalDateEntry = invoice.dates.find(entry => entry.type === 'range' || entry.type === 'date');
                    if (rentalDateEntry) {
                      if (rentalDateEntry.type === 'range' && rentalDateEntry.value && rentalDateEntry.endValue) {
                        // Format as range: DD/MM/YY-DD/MM/YY
                        const startDate = moment(rentalDateEntry.value).format('DD/MM/YY');
                        const endDate = moment(rentalDateEntry.endValue).format('DD/MM/YY');
                        return `${startDate}-${endDate}`;
                      } else if (rentalDateEntry.type === 'date' && rentalDateEntry.value && rentalDateEntry.endValue) {
                        // Legacy support: 'date' type with endValue (should be 'range')
                        const startDate = moment(rentalDateEntry.value).format('DD/MM/YY');
                        const endDate = moment(rentalDateEntry.endValue).format('DD/MM/YY');
                        return `${startDate}-${endDate}`;
                      } else if (rentalDateEntry.type === 'date' && rentalDateEntry.value) {
                        return moment(rentalDateEntry.value).format('DD/MM/YYYY');
                      }
                    }
                  }

                  // Fallback to legacy fields
                  if (invoice.rentalInputType === 'date' && invoice.rentalStartDate && invoice.rentalEndDate) {
                    return `${moment(invoice.rentalStartDate).format('DD/MM/YY')}-${moment(invoice.rentalEndDate).format('DD/MM/YY')}`;
                  }
                  if (invoice.rentalInputType === 'duration' && invoice.rentalDuration && invoice.rentalDurationUnit) {
                    return `${invoice.rentalDuration} ${invoice.rentalDurationUnit}`;
                  }
                }

                // Damaged items specific fields
                if (invoice.invoiceType === 'damaged_items' && invoice.collectionDate) {
                  return moment(invoice.collectionDate).format('DD/MM/YYYY');
                }

                // Legacy service dates (for backward compatibility)
                if (invoice.serviceStartDate && invoice.serviceEndDate && !invoice.serviceInputType) {
                  return `${moment(invoice.serviceStartDate).format('DD/MM/YY')}-${moment(invoice.serviceEndDate).format('DD/MM/YY')}`;
                }

                return 'N/A';
              })()}
            </span>
          </div>
          <div className="invoice-mobile-row">
            <span className="invoice-mobile-label">Status:</span>
            <span className="invoice-mobile-value">
              <Tag color={INVOICE_STATUS_COLORS[invoice.status]}>
                {INVOICE_STATUS_LABELS[invoice.status]}
              </Tag>
            </span>
          </div>
          {invoice.dueDate && (
            <div className="invoice-mobile-row">
              <span className="invoice-mobile-label">Due Date:</span>
              <span className="invoice-mobile-value">
                <span className={`invoice-due-date ${dueDateStatus}`}>
                  {moment(invoice.dueDate).format('DD/MM/YYYY')}
                </span>
              </span>
            </div>
          )}
          <div className="invoice-mobile-row">
            <span className="invoice-mobile-label">Created:</span>
            <span className="invoice-mobile-value">{formatDate(invoice.createdAt)}</span>
          </div>
          {invoice.poNumber && (
            <div className="invoice-mobile-row">
              <span className="invoice-mobile-label">PO Number:</span>
              <span className="invoice-mobile-value">{invoice.poNumber}</span>
            </div>
          )}
          {/* Damaged items specific fields for mobile */}
          {invoice.invoiceType === 'damaged_items' && invoice.collectionDate && (
            <div className="invoice-mobile-row">
              <span className="invoice-mobile-label">Collection Date:</span>
              <span className="invoice-mobile-value">{moment(invoice.collectionDate).format('DD/MM/YYYY')}</span>
            </div>
          )}
          {invoice.invoiceType === 'damaged_items' && invoice.originalInvoiceNumber && (
            <div className="invoice-mobile-row">
              <span className="invoice-mobile-label">Original Invoice:</span>
              <span className="invoice-mobile-value">{invoice.originalInvoiceNumber}</span>
            </div>
          )}
          {invoice.internalRemarks && (
            <div className="invoice-mobile-row" style={{ 
              backgroundColor: '#f6ffed', 
              padding: '8px', 
              borderRadius: '4px',
              border: '1px solid #b7eb8f',
              marginTop: '8px'
            }}>
              <span className="invoice-mobile-label" style={{ color: '#1890ff' }}>
                🔒 Internal Remarks:
              </span>
              <span className="invoice-mobile-value" style={{ 
                fontSize: '12px',
                fontStyle: 'italic',
                display: 'block',
                marginTop: '4px'
              }}>
                {invoice.internalRemarks}
              </span>
            </div>
          )}
        </div>
        
        <div className="invoice-mobile-actions">
          <div className="invoice-mobile-actions-row">
            <button
              className="invoice-mobile-btn primary"
              onClick={() => handleEditInvoice(invoice)}
            >
              <EditOutlined /> Edit
            </button>
            <div className="invoice-mobile-btn">
              <PDFDownloadDropdown 
                invoice={invoice} 
                size="small"
                showText={true}
                buttonProps={{ 
                  style: { 
                    border: 'none', 
                    background: 'transparent', 
                    padding: 0,
                    height: 'auto',
                    color: '#1890ff'
                  } 
                }}
              />
            </div>
            {invoice.email && (
              <button
                className="invoice-mobile-btn"
                onClick={() => handleSendEmail(invoice)}
              >
                <MailOutlined /> Email
              </button>
            )}
            {invoice.tel && (
              <button
                className="invoice-mobile-btn whatsapp"
                onClick={() => handleSendWhatsApp(invoice)}
              >
                <WhatsAppOutlined /> WhatsApp
              </button>
            )}
          </div>
          <div className="invoice-mobile-actions-row">
            <button
              className="invoice-mobile-btn danger"
              onClick={() => handleDeleteInvoice(invoice.id)}
            >
              <DeleteOutlined /> Delete
            </button>
          </div>
          <select
            className="invoice-mobile-status-select"
            value={invoice.status}
            onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
          >
            {Object.entries(INVOICE_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="invoice-list-container">
      <div className="section-header-redesigned">
        <div className="header-top">
          <Title level={2}>Invoice Management</Title>
        </div>
        
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number">{stats.total || 0}</span>
            <span className="stat-label">Total Invoices</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: INVOICE_STATUS_COLORS.draft}}>{stats.draft || 0}</span>
            <span className="stat-label">Draft</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: INVOICE_STATUS_COLORS.sent}}>{stats.sent || 0}</span>
            <span className="stat-label">Sent</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: INVOICE_STATUS_COLORS.paid}}>{stats.paid || 0}</span>
            <span className="stat-label">Paid</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: INVOICE_STATUS_COLORS.overdue}}>{stats.overdue || 0}</span>
            <span className="stat-label">Overdue</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{formatCurrency(stats.totalAmount || 0)}</span>
            <span className="stat-label">Total Amount</span>
          </div>
        </div>
      </div>

      {/* Mobile Stats */}
      <div className="invoice-stats-mobile">
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number">{stats.total || 0}</span>
          <span className="invoice-stat-label">Total</span>
        </div>
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number" style={{color: INVOICE_STATUS_COLORS.draft}}>{stats.draft || 0}</span>
          <span className="invoice-stat-label">Draft</span>
        </div>
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number" style={{color: INVOICE_STATUS_COLORS.sent}}>{stats.sent || 0}</span>
          <span className="invoice-stat-label">Sent</span>
        </div>
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number" style={{color: INVOICE_STATUS_COLORS.paid}}>{stats.paid || 0}</span>
          <span className="invoice-stat-label">Paid</span>
        </div>
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number" style={{color: INVOICE_STATUS_COLORS.overdue}}>{stats.overdue || 0}</span>
          <span className="invoice-stat-label">Overdue</span>
        </div>
        <div className="invoice-stat-mobile">
          <span className="invoice-stat-number">{formatCurrency(stats.totalAmount || 0)}</span>
          <span className="invoice-stat-label">Amount</span>
        </div>
      </div>

      {/* Mobile Create Buttons */}
      <div className="invoice-create-buttons-mobile">
        <button
          className="invoice-create-btn-mobile"
          onClick={handleCreateFromQuotation}
        >
          <FileTextOutlined /> From Quotation
        </button>
        <button
          className="invoice-create-btn-mobile primary"
          onClick={handleCreateInvoice}
        >
          <PlusOutlined /> Create Invoice
        </button>
      </div>

      {/* Mobile Filters */}
      <div className="invoice-filters-mobile">
        <input
          className="invoice-search-mobile"
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="invoice-filters-row">
          <select
            className="invoice-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            {Object.entries(INVOICE_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            className="invoice-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {Object.entries(INVOICE_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop View */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Search invoices..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col span={3}>
              <Select
                placeholder="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">All Status</Option>
                {Object.entries(INVOICE_STATUS_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Type"
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">All Types</Option>
                {Object.entries(INVOICE_TYPE_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Accounting"
                value={accountingFilter}
                onChange={setAccountingFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">All</Option>
                <Option value="submitted">✓ Submitted</Option>
                <Option value="pending">○ Pending</Option>
              </Select>
            </Col>
            <Col span={9}>
              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={handleCSVImport}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    📊 CSV Import
                  </Button>
                  <Button
                    type="default"
                    icon={<FileTextOutlined />}
                    onClick={handleCreateFromQuotation}
                  >
                    From Quotation
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateInvoice}
                  >
                    Create Invoice
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </div>

        <Table
          dataSource={filteredInvoices}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: filteredInvoices.length,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} invoices`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize
              }));
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({
                ...prev,
                current: 1, // Reset to first page when changing page size
                pageSize: size
              }));
            }
          }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              )
          }}
        />
      </Card>

      {/* Mobile View */}
      <div className="invoice-mobile-view">
        {loading ? (
          <div className="invoice-mobile-loading">Loading invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="invoice-mobile-empty">
            <div className="invoice-mobile-empty-icon">📄</div>
            <div className="invoice-mobile-empty-text">No invoices found</div>
            <div className="invoice-mobile-empty-subtext">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'Create your first invoice to get started.'}
            </div>
          </div>
        ) : (
          filteredInvoices.map(renderMobileCard)
        )}
      </div>

      {/* Create Invoice Modal */}
      <Modal
        title={selectedQuotation ? "Create Invoice from Quotation" : "Create New Invoice"}
        open={showCreateModal}
        onCancel={handleCancelModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <InvoiceForm
          linkedQuotation={selectedQuotation}
          onSave={handleSaveInvoice}
          onCancel={handleCancelModal}
        />
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        title="Edit Invoice"
        open={showEditModal}
        onCancel={handleCancelModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {selectedInvoice && (
          <InvoiceForm
            invoice={selectedInvoice}
            onSave={handleSaveInvoice}
            onCancel={handleCancelModal}
          />
        )}
      </Modal>

      {/* Quotation Selection Modal */}
      <Modal
        title="Select Quotation to Create Invoice"
        open={showQuotationSelectModal}
        onCancel={handleCancelModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Select a quotation to create an invoice. Only quotations that haven't been invoiced yet and are not rejected are shown.
          </Text>
        </div>
        <Table
          dataSource={quotations}
          columns={quotationColumns}
          rowKey="id"
          size="small"
          pagination={{
            pageSize: 5,
            showSizeChanger: false
          }}
        />
      </Modal>

      {/* WhatsApp Modal */}
      <WhatsAppModal
        visible={showWhatsAppModal}
        onCancel={handleCancelModal}
        documentData={whatsAppInvoice}
        documentType="invoice"
        onSendSuccess={handleWhatsAppSendSuccess}
      />

      {/* CSV Import Modal */}
      <Modal
        title="Import Historical Invoices"
        open={showCSVImportModal}
        onCancel={handleCancelModal}
        footer={null}
        width={900}
        destroyOnClose
      >
        <CSVInvoiceImport
          onClose={handleCancelModal}
          onSuccess={handleCSVImportSuccess}
        />
      </Modal>

      {/* Email Modal */}
      {emailInvoice && (
        <EmailModal
          visible={showEmailModal}
          onCancel={() => setShowEmailModal(false)}
          documentType="invoice"
          documentData={emailInvoice}
          onSendSuccess={handleEmailSendSuccess}
        />
      )}
    </div>
  );
};

export default InvoiceList;
